import axios, {AxiosError, AxiosInstance } from 'axios';
import {
  ModelCreation,
  Model,
  PostRequestResponse,
  FileUploadResponse,
  TrainingSubmissionResponse,
  TrainingStatusResponse,
  ModelResponse,
  TrainingInfoResponse,
  ImageResponse,
  PaymentDetailsResponse,
  PlanResponse,
  PaymentDetails
} from "./types";

// export * from "./types";

class ApiError extends Error {
  status?: number;
  fullError?: AxiosError;
  data?: any;

  constructor(message: string, status?: number, fullError?: AxiosError, data?: any) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.fullError = fullError;
    this.data = data;
  }
}

const baseURL = process.env.REACT_APP_BASE_URL;

console.log("baseURL", baseURL);

const apiClient: AxiosInstance = axios.create({
  baseURL: baseURL,
  timeout: 40000,
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
  },
});

apiClient.interceptors.request.use((config) => {
  const accessToken = localStorage.getItem("accessToken");
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  } else {
    window.location.href = "/login";
  }
  return config;
});

const generateApiWithRetryFormData= async (url: any, method: any, data?: any,) => {
  const localStorageData = localStorage.getItem("accessToken");
  const options = {
    method: method,
    timeout: 280000,
    headers: {
      Authorization: `Bearer ${localStorageData}`,
      "content-type": "multipart/form-data",

    },
    data: method === "POST" && data ? data : undefined,
  };

  try {
    const response = await apiClient(url, options);
    return response; // Return the response if successful
  } catch (error: any) {
    const status = error?.response?.status;
    const message = error?.response?.data?.message;
    // Only refresh token for 401 or 403 (not 400)
    if (
      status === 401 ||
      (status === 403 &&
        message !== "Insufficient credits" &&
        message !== "Insufficient credits for video generation" &&
        message !== "Credits Not Found")
    ) {
      try {
        const refreshToken = localStorage.getItem("refreshToken");
        const accessToken = localStorage.getItem("accessToken");
        const refreshResponse = await axios.post(`${baseURL}user/generateAccessToken`, {
          token: accessToken,
          refresh_token: refreshToken,
        });
        //Storing generate token here
        const newAccessToken = refreshResponse.data?.data?.token;
        localStorage.setItem("accessToken", newAccessToken);

        const retryOptions = {
          ...options,
          headers: {
            ...options.headers,
            Authorization: `Bearer ${newAccessToken}`,
          },
        };
        const retryResponse = await apiClient(url, retryOptions);
        return retryResponse;
      } catch (refreshError: any) {
        console.error("refreshError", refreshError);
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        window.location.href = "/login";
        throw refreshError;
      }
    } else {
      throw error; // Re-throw error for further handling
    }
  }
};

//Generate Access New token if token is expired function:
const generateApiWithRetry = async (url: any, method: any, data?: any, encryptRedirectToken?: any) => {
  const localStorageData = localStorage.getItem("accessToken");
const headers: any = {
  "content-type": "application/json",
  Authorization: `Bearer ${localStorageData}`,  
};


const options = {
  method: method,
  timeout: 280000,
  headers,
  data: method === "POST" && data ? data : undefined,
};

  try {
    const response = await apiClient(url, options);
    return response; // Return the response if successful
  } catch (error: any) {
    const status = error?.response?.status;
    const message = error?.response?.data?.message;
    // Only refresh token for 401 or 403 (not 400)
    if (
      status === 401 ||
      (status === 403 &&
        message !== "Insufficient credits" &&
        message !== "Insufficient credits for video generation" &&
        message !== "Credits Not Found")
    ) {
      try {
        const refreshToken = localStorage.getItem("refreshToken");
        const accessToken = localStorage.getItem("accessToken");
        const refreshResponse = await axios.post(`${baseURL}user/generateAccessToken`, {
          token: accessToken,
          refresh_token: refreshToken,
        });
        //Storing generate token here
        const newAccessToken = refreshResponse.data?.data?.token;
        localStorage.setItem("accessToken", newAccessToken);

        const retryOptions = {
          ...options,
          headers: {
            ...options.headers,
            Authorization: `Bearer ${newAccessToken}`,
          },
        };
        const retryResponse = await apiClient(url, retryOptions);
        return retryResponse;
      } catch (refreshError: any) {
        console.error("refreshError", refreshError);
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        window.location.href = "/login";
        throw refreshError;
      }
    } else {
      throw error; // Re-throw error for further handling
    }
  }
};





const handleApiError = (error: unknown): never => {
  if (axios.isAxiosError(error)) {
    const message =
      error.response?.data?.message ||
      error.message ||
      "An unexpected error occurred";
    const status = error.response?.status;
    const data = error.response?.data || null;

    // Throw the custom error with all relevant information
    throw new ApiError(message, status, error, data);
  } else {
    // Handle non-Axios errors
    throw new ApiError("An unexpected error occurred", undefined, error as AxiosError);
  }
};

export const createModel = async (
  data: ModelCreation
): Promise<PostRequestResponse> => {
  try {
    const response = await generateApiWithRetry("/model/create", "POST", data);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const getModels = async (userId: string, tryout: boolean = false): Promise<Model[]> => {
  try {
    const endpoint = tryout
      ? `/model/allModels/${userId}/tryout`  
      : `/model/allModels/${userId}`;      

    const response = await generateApiWithRetry(endpoint, "GET");
    return response.data.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const getModel = async (modelId: string): Promise<Model> => {
  try {
    const response = await generateApiWithRetry("/model/" + modelId, "GET");;
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const uploadFile = async (
  uploadFile: any
): Promise<FileUploadResponse> => {
  try {
    const response = await generateApiWithRetryFormData(
      "/uploadFile/uploadSupportingFile",
      "POST",
      uploadFile
    );
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const trainImages = async (
  trainData: any
): Promise<TrainingSubmissionResponse> => {
  try {
    const response = await generateApiWithRetry("training/train", "POST", trainData);
    return response.data;
  } catch (error) {
    console.log("Error", error)
    handleApiError(error);
    throw error;
  }
};

export const getTrainingStatus = async (
  trainingId: string
): Promise<TrainingStatusResponse> => {
  try {
    const response = await generateApiWithRetry("training/status/" + trainingId, "GET");
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const ModelDetails = async (modelId: string): Promise<ModelResponse> => {
  try {
    const response = await generateApiWithRetry("training/all/" + modelId, "GET");
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const TrainingInfo = async (
  trainingId: string
): Promise<TrainingInfoResponse> => {
  try {
    const response = await generateApiWithRetry("training/info/" + trainingId, "GET");
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const getImagesByModal = async (
  modelId: string
): Promise<ImageResponse> => {
  try {
    const response = await generateApiWithRetry(
      "uploadFile/fetchModelImages/" + modelId, "GET"
    );
    return response.data;
  } catch (error) {
    handleApiError(error);
    console.log("error", error);
    throw error;
  }
};

export const postGenerateImages = async (generateData: any): Promise<ImageResponse> => {
  try {
    const response = await generateApiWithRetry("uploadFile/generateSmallImage", "POST", generateData);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}


export const getGenereatedImages = async (userId: any, trainingId: string, tryout: boolean = false): Promise<any> => {
  try {
    const endpoint = tryout
      ? `/images/search/${userId}/${trainingId}/tryout`  
      : `/images/search/${userId}/${trainingId}`;         

    const response = await generateApiWithRetry(endpoint, "GET");
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}

export const getProductPlanList = async (type?: string): Promise<PlanResponse> => {
  try {
    const data = type ? { type } : {};
    const response = await axios.post(`${baseURL}stripeProducts/productList`, Object.keys(data).length ? data : undefined);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}
export const getProductPlanListWeb = async (): Promise<PlanResponse> => {
  try {
    const response = await generateApiWithRetry(`stripeProducts/productList`, 'POST');
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}

export const createCheckoutSession = async (product: PaymentDetails): Promise<PaymentDetailsResponse> => {
  try {
    const response = await generateApiWithRetry("stripeProducts/create-checkout-session", "POST",  product );
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}

export const phonePeCheckoutSession = async(product: PaymentDetails): Promise<any> => { 
  try {
    const response = await generateApiWithRetry("phonePeProducts/create-checkout-session", "POST", product );
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}

// Login API
export const LoginOperation = async (obj: any): Promise<any> => {
  try {
    const response = await axios.post(`${baseURL}user/login`, obj);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}

// Generate access reresh token
export const generateToken = async (): Promise<any> => {
  const token = localStorage.getItem('refreshToken');
  const refreshToken = localStorage.getItem('refreshToken');
  try {
    const response = await apiClient.post(`${baseURL}user/generateAccessToken`, {
      token: token,
      refresh_token: refreshToken
    })
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}

export const login = async (data: any) => {
  try {
    const response = await apiClient.post('/user/login', data);
    return response;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}

export const signup = async (data: any) => { 
  try {
    const response = await axios.post(`${baseURL}user/register`, data);
    return response;
  } catch (error) { 
    handleApiError(error);
    throw error;
  }
}

export const verification = async (data: any) => { 
  try {
    const response = await axios.post(`${baseURL}user/verifyRegister`, data);
    return response.data;
  } catch (error) { 
    handleApiError(error);
    throw error;
  }
}

//creditPoints api
export const getCreditPoints = async (userId: string): Promise<any> => {
  try {
    const response = await generateApiWithRetry('stripeProducts/getCreditPoints', "POST", { userId: userId });
    return response.data
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const logout = async (accessToken : any, refreshToken:any) => { 
  try {
    const response = await generateApiWithRetry('/user/logout', "POST", {
      accessToken: accessToken,
      refreshToken: refreshToken
    });
    return response.data;
  } catch (error) { 
    handleApiError(error);
    throw error;
  } 
}

export const changePassword = async (data: any) => {
  try {
    const response = await generateApiWithRetry('/user/change-password', "POST", data);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}

export const forgotPasswordRes = async (data: any) => {
  try {
    const forgotPasswordResponse = await axios.post(`${baseURL}user/forgot-password`, data);
    return forgotPasswordResponse.data;
  } catch (error) {
    console.error("Error fetching data:", error);
    handleApiError(error);
    throw error;
  }
};

export const resetPasswordRes = async (data: any) => {
  try {
    const resetPasswordResponse = await axios.post(`${baseURL}user/reset-password`, data);
    return resetPasswordResponse.data;
  } catch (error) {
    console.error("Error fetching data:", error);
    handleApiError(error);
    throw error;
  }
};

export const getPresets = async(eventId:string) => {
  try {
    const response = await generateApiWithRetry(`events/getEventDetailsbyId`, "POST", { eventId: eventId });
    return response.data.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}

export const getUploadedImages = async(userId:any, eventId?:any) => {
  try {
    const response = await generateApiWithRetry(`uploadFile/getUploadedImageByFilter`, "POST", { userId : userId, eventId : eventId, isGenerated : false });
    return response.data.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}

export const getGeneratedImagesByEvent = async(userId:any, eventId?:any) => {
  try {
    const response = await generateApiWithRetry(`images/search`, "POST", { userId : userId, eventId : eventId });
    return response.data.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}

export const eventImageUpload = async(data:any) => {
  try {
    const response = await generateApiWithRetryFormData(`uploadFile/uploadSupportingFile`, "POST", data);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}

export const generateEventImages = async(data:any) => { 
  try {
    const response = await generateApiWithRetry(`images`, "POST", data);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}

//Enhance Image API
export const enhancePrompt = async(data:any) => {
  try{
    const response = await generateApiWithRetry(`uploadFile/enhancePrompt`, "POST", data);
    return response.data;
  }
  catch(error){
    handleApiError(error);
    throw error;
  }
}
export const enhancePromptVariation = async(data:any) => {
  try{
    const response = await generateApiWithRetry(`uploadFile/promptVariant`, "POST", data);
    return response.data;
  }
  catch(error){
    handleApiError(error);
    throw error;
  }
}

// Generate Video API
export const generateVideo = async(data:any) => {
  try{
    const response = await generateApiWithRetry(`videos`, "POST", data);
    return response.data;
  }
  catch(error){
    handleApiError(error);
    throw error;
  }
}

// all video search api
export const generateSearchVideo = async(data:any) => {
  try{
    const response = await generateApiWithRetry(`videos/search`, "POST", data);
    return response.data;
  }
  catch(error){
    handleApiError(error);
    throw error;
  }
}
export const generateVideoId = async(data:any) => {
  try{
    const response = await generateApiWithRetry(`videos/${data}`, "GET");
    return response.data;
  }
  catch(error){
    handleApiError(error);
    throw error;
  }
}
// all modelId get api
export const getModelName = async(mediaType:string) => {
  try{
    const response = await generateApiWithRetry(`events?mediaType=${mediaType}`, "GET");
    return response.data;
  }
  catch(error){
    handleApiError(error);
    throw error;
  }
}

export const generateCartoonImages = async(data:any) => {
  try{
    const response = await generateApiWithRetry(`images`, "POST", data);
    return response.data;
  }
  catch(error){
    handleApiError(error);
    throw error;
  }
}

// generate video Podcast sending formData
export const generateVideoPodcast = async(formData:any) => {
  try{
    const response = await generateApiWithRetryFormData(`videos`, "POST", formData);
    return response.data;
  }
  catch(error){
    handleApiError(error);
    throw error;
  }
}

export const generateFilterVideos = async(userId:any) => {
  try{
    const response = await generateApiWithRetry(`videos/search`, "POST", { userId: userId });
    return response.data;
  }
  catch(error){
    handleApiError(error);
    throw error;
  }
}

// Receipt API
export const getUsedDetails = async (apiName: string,userId: string, limits:number, page: number): Promise<any> => {
  try {
    const response = await generateApiWithRetry(`user/${apiName}?userId=${userId}&limit=${limits}&page=${page}`, "GET");
    return response;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}

// Library API
export const getLibraryDetails = async (userId: string, ): Promise<any> => {
  try {
    const response = await generateApiWithRetry(`user/assets?userId=${userId}`, "GET");
    return response;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}
export const getLibraryDetailsAll = async (userId: string, limit: number, page: number): Promise<any> => {
  try {
    console.log('Calling getLibraryDetailsAll with:', { userId, limit, page });
    const response = await generateApiWithRetry(`user/assets?userId=${userId}&limit=${limit}&page=${page}`, "GET");
    console.log('API response:', response);
    return response;
  } catch (error) {
    console.error('getLibraryDetailsAll error:', error);
    handleApiError(error);
    throw error;
  }
}
export const getLibraryDetailsType = async (userId: string, type: string, limit: number, page: number): Promise<any> => {
  try {
    console.log('Calling getLibraryDetailsAll with:', { userId, limit, page });
    const response = await generateApiWithRetry(`user/assets?userId=${userId}&type=${type}&limit=${limit}&page=${page}`, "GET");
    console.log('API response:', response);
    return response;
  } catch (error) {
    console.error('getLibraryDetailsAll error:', error);
    handleApiError(error);
    throw error;
  }
}
export const getLibraryEventDetails = async (userId: string, eventId:string): Promise<any> => {
  try {
    const response = await generateApiWithRetry(`user/assets?userId=${userId}&eventId=${eventId}&isGenerated=true`, "GET");
    return response;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}
//  showcase filter api
export const getLibraryFilterShowcase = async (userId: string, type:string, eventId:string): Promise<any> => {
  try {
    const response = await generateApiWithRetry(`user/assets?userId=${userId}&type=${type}&eventId=${eventId}`, "GET");
    return response;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}
export const getLibraryFilterShowcaseVariation = async (
  userId: string,
  type: string,
  eventIds: string[]
): Promise<any> => {
  try {
    const eventIdParams = eventIds.map(id => `eventId=${encodeURIComponent(id)}`).join('&');
    const response = await generateApiWithRetry(
      `user/assets?userId=${userId}&type=${type}&${eventIdParams}`,
      "GET"
    );
    return response;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};


// AI-Influencer API
export const createInfluencer = async (data: any): Promise<any> => {
  try {
    const response = await generateApiWithRetryFormData("images", "POST", data);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}

export const influncerSelectedImages = async (data: any): Promise<any> => {
  try {
    const response = await generateApiWithRetryFormData("images/selectImages", "POST", data);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}
export const influncerSelectedImagesVariation = async (data: any): Promise<any> => {
  try {
    const response = await generateApiWithRetry("images/selectImages", "POST", data);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}

// audio api details
export const fetchAudioDetails = async (data: any): Promise<any> => {
  try {
    const response = await generateApiWithRetry(`audio/create`, "POST", data);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}

// get all audio details
export const fetchAllAudioDetails = async (type:string,eventId: string, userId: string): Promise<any> => {
  try {
    const response = await generateApiWithRetry(`user/assets?type=${type}&eventId=${eventId}&userId=${userId}`, "GET");
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}

// youtube connector API
export const youtubeConnectors = async (status: string, userId:string, channelId?:string): Promise<any> => {
  try {
    // Only add channelId param if it is defined
    const channelIdParam = channelId ? `&channelId=${channelId}` : '';
    const response = await generateApiWithRetry(`youtube?status=${status}&userId=${userId}${channelIdParam}`, "GET");
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}

// upload youtube video
export const uploadYoutubeVideo = async (data: any): Promise<any> => {
  try {
    // const response = await generateApiWithRetry("youtube/upload", "POST", data);
    const response = await axios.post(`${baseURL}youtube/upload`, data)
        
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}

export  const youTubeChannelList = async(userId:string) =>{
  try{
    const response = await generateApiWithRetry(`youtube/channels?userId=${userId}`, "GET");
    return response.data;
  } catch (error) {
  }
} 

// all response
export const getAllChannel = async (): Promise<any> => {
  try {
    const response = await generateApiWithRetry(`youtube/oauth2callback`, "GET");
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}

// delete api
export const deleteImages = async (mediaid: string, type:string, userId:string): Promise<any> => {
  try {
    const response = await generateApiWithRetry(`user/deleteMedia?mediaId=${mediaid}&type=${type}&userId=${userId}`, "DELETE");
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}

// updatedData for Youtube
export const updateYoutubeData = async (videoId: any, accessToken:string, refreshToken: string): Promise<any> => {
  try {
    const response = await generateApiWithRetry(`youtube/uploadData?videoId=${videoId}&accessToken=${accessToken}&refreshToken=${refreshToken}`, "GET");
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}

// get all audio details
export const getAllAudioDetails = async (): Promise<any> => {
  try {
    const response = await generateApiWithRetry(`audio/search?type=ai`, "GET");
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}

export const googleSignin = async (): Promise<any> => {
  try {
    const response = await axios.get(`${baseURL}user/google-login`, {
      headers:{
        "Content-Type": "application/json",
        "app-type": "website",
      }
    });
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}

// reels json
export const videoReelsEditJson = async (id: string): Promise<any> => {
  try {
    const reelsResponse = await generateApiWithRetry(`videos/json/${id}`, "GET");
    return reelsResponse.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}

export const saveReelsVideo = async (data: any): Promise<any> => {
  try {
    const response = await generateApiWithRetry(`videos/start-render`, "POST", data);
    return response.data;
  }
  catch (error) {
    handleApiError(error);
    throw error;
  }
}

export const orderStatus = async(orderId: string) =>{
  try{
    const response = await generateApiWithRetry(`user/order-status?orderId=${orderId}`, "GET");
    return response.data;
  }catch (error) {
    handleApiError(error);
    throw error;
  }
}

// voice-clone
export const voiceClone = async(data: any) =>{
  try{
    // Pass FormData directly, not wrapped in an object
    const response = await generateApiWithRetryFormData(`audio/clone-voice`, "POST", data);
    console.log("voiceClone response:", response.data, response);
    return response.data;
  }catch (error){
    handleApiError(error);
    throw error;
  }
}
export const deleteAudio = async (mediaid?: string, type?:string, modelId?:string, eventId?:string): Promise<any> => {
  try {
    const response = await generateApiWithRetry(`user/deleteMedia?mediaId=${mediaid}&type=${type}&modelId=${modelId}&eventId=${eventId}`, "DELETE");
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}
export const deleteAudioAI = async ( type?:string, modelType?:string, modelId?:string, eventId?:string): Promise<any> => {
  try {
    const response = await generateApiWithRetry(`user/deleteMedia?type=${type}&modelType=${modelType}&modelId=${modelId}`, "DELETE");
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}
export const getAudioUpdateDetails = async(data: any): Promise<any> => {
  try {
    const response = await generateApiWithRetry(`audio/update-voice`, "POST", data);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}

export const createCheckoutSessionToken = async (product: PaymentDetails, encryptRedirectToken: string | null): Promise<PaymentDetailsResponse> => {
  try {
    const response = await axios.post(`${baseURL}stripeProducts/create-checkout-session`, product, {  
      headers: {
        "Content-Type": "application/json",
        "x-redirect-token": encryptRedirectToken ? encryptRedirectToken : "",
      },
    });
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}

export const phonePeCheckoutSessionToken = async(product: PaymentDetails, encryptRedirectToken: string | null): Promise<any> => { 
  try {
    const response = await axios.post(`${baseURL}phonePeProducts/create-checkout-session`, product, {
      headers: {
        "Content-Type": "application/json",
        "x-redirect-token": encryptRedirectToken ? encryptRedirectToken : "",
      },
    });
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}

export const resendOTP = async (data:any) => {
  try {
    const response = await axios.post(`${baseURL}user/resend-otp`, data);
    return response.data;
  } catch (error:any) {
    console.error("resendOTP error", error);
    handleApiError(error?.data || error);
    throw error;
  }
};